import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CompanyService } from 'src/app/company/company.service';
import { ExcelUtility } from 'src/app/shared/ExcelUtility';

type TemplateType = 'plain' | 'group';

@Injectable({ providedIn: 'root' })
export class PrfTemplateDownloadService {
  constructor(private readonly companyService: CompanyService) {}

  // Downloads a blank PRF template with no group data.
  async downloadPlainTemplate(prfyear: number): Promise<void> {
    const url = await this.getTemplateUrl();
    const workbook = await ExcelUtility.loadFromUrl(url);

    // The template ships with two "Group Reporting" variants. Keep the plain one
    // (without the Company Name / Include columns) and drop the group specific one.
    this.keepGroupReportingVariant(workbook, 'plain');

    this.writeTemplateTypeToMeta(workbook, 'plain');
    await ExcelUtility.save(workbook, 'PRF_Template');
  }

  // Downloads a template pre-filled with the given group's company details.
  async downloadGroupTemplate(prfyear: number, groupcode: string): Promise<void> {
    const url = await this.getTemplateUrl();
    const workbook = await ExcelUtility.loadFromUrl(url);

    // The template ships with two "Group Reporting" variants. Keep the group
    // specific one (with the Company Name / Include columns) and drop the plain one.
    this.keepGroupReportingVariant(workbook, 'group');

    await this.fillGroupReportingData(workbook, prfyear, groupcode);
    this.writeTemplateTypeToMeta(workbook, 'group');
    await ExcelUtility.save(workbook, `PRF_Template_Group_${groupcode}`);
  }

  // The downloaded template contains two "Group Reporting" sheets: a plain variant
  // and a group specific variant (identified by its "Include" column). This removes
  // the unwanted variant and names the remaining one exactly "Group Reporting".
  private keepGroupReportingVariant(workbook: any, keep: TemplateType): void {
    const sheets = workbook.worksheets();
    const groupReportingSheets: any[] = [];
    for (let i = 0; i < sheets.count; i++) {
      const sheet = workbook.worksheets(i);
      if (sheet?.name && String(sheet.name).trim().toLowerCase().includes('group reporting')) {
        groupReportingSheets.push(sheet);
      }
    }

    if (groupReportingSheets.length > 1) {
      const groupVariant =
        groupReportingSheets.find((sheet) => this.hasHeader(sheet, 'include')) ??
        groupReportingSheets[groupReportingSheets.length - 1];
      const plainVariant =
        groupReportingSheets.find((sheet) => sheet !== groupVariant) ??
        groupReportingSheets[0];

      const sheetToRemove = keep === 'group' ? plainVariant : groupVariant;
      sheets.remove(sheetToRemove);
    }

    // Ensure the remaining sheet is named exactly "Group Reporting".
    const remaining = this.findWorksheetByName(workbook, 'group reporting');
    if (remaining) {
      remaining.name = 'Group Reporting';
    }
  }

  private hasHeader(worksheet: any, headerText: string): boolean {
    const target = headerText.toLowerCase();
    for (let col = 0; col < 50; col++) {
      const value = worksheet.rows(0).getCellValue(col);
      if (value != null && String(value).trim().toLowerCase().includes(target)) {
        return true;
      }
    }
    return false;
  }

  private async getTemplateUrl(): Promise<string> {
    const resp: any = await firstValueFrom(
      this.companyService.getTemplateFile('ful_template')
    );
    if (resp?.message !== 'Success' || !resp?.templateurl) {
      throw new Error('Failed to load template file.');
    }
    return resp.templateurl;
  }

  private async fillGroupReportingData(
    workbook: any,
    prfyear: number,
    groupcode: string
  ): Promise<void> {
    const resp: any = await firstValueFrom(
      this.companyService.loadGroupReport(prfyear, groupcode, groupcode)
    );
    if (resp?.message !== 'Success') {
      throw new Error('Failed to load group company details.');
    }

    const details: any[] = resp.groupcompanydetails ?? [];
    const worksheet = this.findWorksheetByName(workbook, 'group reporting');
    if (details.length === 0 || !worksheet) {
      return;
    }

    const pdbCol = this.findColumnIndex(worksheet, 'pdb number', 0);
    const nameCol = this.findColumnIndex(worksheet, 'company name', 1);
    const includeCol = this.findColumnIndex(worksheet, 'include', 2); 

    let rowIndex = 1;
    for (const detail of details) {
      worksheet.rows(rowIndex).setCellValue(pdbCol, detail.No ?? '');
      worksheet.rows(rowIndex).setCellValue(nameCol, detail.Name ?? '');
      worksheet.rows(rowIndex).setCellValue(includeCol, detail.Include ?? 'NO');
      rowIndex++;
    }
  }

  private findColumnIndex(worksheet: any, headerText: string, fallback: number): number {
    const target = headerText.toLowerCase();
    for (let col = 0; col < 50; col++) {
      const value = worksheet.rows(0).getCellValue(col);
      if (value != null && String(value).trim().toLowerCase().includes(target)) {
        return col;
      }
    }
    return fallback;
  }

  // Store the downloaded template type in the meta sheet at cell A90.
  private writeTemplateTypeToMeta(workbook: any, templateType: TemplateType): void {
    const metaSheet = this.findWorksheetByName(workbook, 'meta');
    if (!metaSheet) {
      return;
    }
    const value = templateType === 'plain' ? 'Plane' : 'GroupSpecific';
    metaSheet.rows(89).setCellValue(0, value);
  }

  private findWorksheetByName(workbook: any, namePart: string): any {
    const sheets = workbook.worksheets();
    const target = namePart.toLowerCase();
    for (let i = 0; i < sheets.count; i++) {
      const sheet = workbook.worksheets(i);
      if (sheet?.name && String(sheet.name).trim().toLowerCase().includes(target)) {
        return sheet;
      }
    }
    return null;
  }
}
